import {
  FETCH_CART_VOUCHER_ITEMS,
  ADD_VOUCHER_TO_CART,
  UPDATE_CART_VOUCHER_ITEM,
  REMOVE_CART_VOUCHER_ITEM,
  ACCEPT_VOUCHER_TERMS,
  CLEAR_CART_VOUCHER_ERRORS,
  CLEAR_CART_VOUCHER_ITEMS,
} from '../actions/action_cart_voucher_items';

const initialState = Object.freeze({
  items: [],
  voucherSubtotalCents: 0,
  voucherItemsCount: 0,
  allTermsAccepted: true,
  loading: false,
  error: null,
  addingToCart: false,
  addToCartError: null,
});

export default function cartVoucherItemsReducer(state = initialState, action) {
  Object.freeze(state);

  switch (action.type) {
    // Pending states
    case `${FETCH_CART_VOUCHER_ITEMS}_PENDING`:
      return { ...state, loading: true, error: null };

    case `${UPDATE_CART_VOUCHER_ITEM}_PENDING`:
    case `${ACCEPT_VOUCHER_TERMS}_PENDING`:
      return { ...state, loading: true };

    // Fetch cart voucher items
    case FETCH_CART_VOUCHER_ITEMS:
      if (action.payload && action.payload.data) {
        return {
          ...state,
          loading: false,
          items: action.payload.data.cart_voucher_items || [],
          voucherSubtotalCents: action.payload.data.voucher_subtotal_cents || 0,
          voucherItemsCount: action.payload.data.voucher_items_count || 0,
          allTermsAccepted: action.payload.data.all_terms_accepted || false,
          error: null,
        };
      } else if (action.error) {
        return {
          ...state,
          loading: false,
          error:
            action.payload?.response?.data?.error ||
            'Failed to load cart voucher items',
        };
      }
      return state;

    // Add voucher to cart
    case ADD_VOUCHER_TO_CART:
      if (action.error) {
        return {
          ...state,
          addingToCart: false,
          addToCartError: action.payload,
        };
      }
      if (action.payload && action.payload.data) {
        // Add the new item to state and update totals
        const newItem = action.payload.data;
        const existingItemIndex = state.items.findIndex(
          (item) => item.id === newItem.id,
        );
        let updatedItems;
        if (existingItemIndex >= 0) {
          // Item already exists (quantity update), replace it
          updatedItems = state.items.map((item, index) =>
            index === existingItemIndex ? newItem : item,
          );
        } else {
          // New item, add to array
          updatedItems = [...state.items, newItem];
        }
        // Recalculate totals
        const newSubtotalCents = updatedItems.reduce(
          (sum, item) => sum + (item.subtotal_cents || 0),
          0,
        );
        const newItemsCount = updatedItems.reduce(
          (sum, item) => sum + (item.quantity || 0),
          0,
        );
        return {
          ...state,
          items: updatedItems,
          voucherSubtotalCents: newSubtotalCents,
          voucherItemsCount: newItemsCount,
          addingToCart: false,
          addToCartError: null,
        };
      }
      return state;

    // Update cart voucher item
    case UPDATE_CART_VOUCHER_ITEM:
      if (action.payload && action.payload.data) {
        const updatedItem = action.payload.data;
        const updatedItems = state.items.map((item) =>
          item.id === updatedItem.id ? updatedItem : item,
        );
        return {
          ...state,
          loading: false,
          items: updatedItems,
          error: null,
        };
      } else if (action.error) {
        return {
          ...state,
          loading: false,
          error:
            action.payload?.response?.data?.error ||
            'Failed to update cart voucher item',
        };
      }
      return state;

    // Remove cart voucher item
    case REMOVE_CART_VOUCHER_ITEM:
      if (action.meta && action.meta.itemId) {
        const filteredItems = state.items.filter(
          (item) => item.id !== action.meta.itemId,
        );
        return {
          ...state,
          items: filteredItems,
          voucherItemsCount: filteredItems.reduce(
            (sum, item) => sum + item.quantity,
            0,
          ),
          voucherSubtotalCents: filteredItems.reduce(
            (sum, item) => sum + item.subtotal_cents,
            0,
          ),
        };
      }
      return state;

    // Accept terms for a voucher item
    case ACCEPT_VOUCHER_TERMS:
      if (action.payload && action.payload.data) {
        const acceptedItem = action.payload.data;
        const itemsWithTerms = state.items.map((item) =>
          item.id === acceptedItem.id ? acceptedItem : item,
        );
        const allAccepted = itemsWithTerms.every((item) => item.terms_accepted);
        return {
          ...state,
          loading: false,
          items: itemsWithTerms,
          allTermsAccepted: allAccepted,
          error: null,
        };
      } else if (action.error) {
        return {
          ...state,
          loading: false,
          error:
            action.payload?.response?.data?.error || 'Failed to accept terms',
        };
      }
      return state;

    // Clear errors
    case CLEAR_CART_VOUCHER_ERRORS:
      return {
        ...state,
        error: null,
        addToCartError: null,
      };

    // Clear all voucher items (when cart becomes empty)
    case CLEAR_CART_VOUCHER_ITEMS:
      return {
        ...initialState,
      };

    default:
      return state;
  }
}
